import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Mail, Phone, Globe, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { contactInfo } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setFormData({ name: '', email: '', phone: '', message: '' });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 
                          'An error occurred. Please try again or contact us directly at ' + contactInfo.email;
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Eaglesway Group</title>
        <meta 
          name="description" 
          content="Get in touch with Eaglesway Group. Contact our team in UAE, UK, or USA for global trading, procurement, logistics, and marketing solutions. We're here to help your business grow." 
        />
        <meta name="keywords" content="contact eaglesway, international trading inquiry, global logistics contact, business partnership, procurement services UAE UK USA" />
      </Helmet>
      
      <div className="min-h-screen bg-black text-white pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1
              className={`text-4xl md:text-5xl font-bold mb-6 transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              Contact <span className="text-[#D4AF37]">Us</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Connect with our team to discuss your global trading, logistics, and marketing needs
            </p>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div>
                <h2 className="text-3xl font-bold text-white mb-8">
                  {contactInfo.company}
                </h2>
                
                <div className="space-y-6 mb-12">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <Phone className="w-6 h-6 text-[#D4AF37]" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">Phone</h3>
                      <a
                        href={`tel:${contactInfo.phone}`}
                        className="text-gray-400 hover:text-[#D4AF37] transition-colors"
                      >
                        {contactInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <Mail className="w-6 h-6 text-[#D4AF37]" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">Email</h3>
                      <a
                        href={`mailto:${contactInfo.email}`}
                        className="text-gray-400 hover:text-[#D4AF37] transition-colors"
                      >
                        {contactInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <Globe className="w-6 h-6 text-[#D4AF37]" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">Website</h3>
                      <a
                        href={`https://${contactInfo.website}`}
                        className="text-gray-400 hover:text-[#D4AF37] transition-colors"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {contactInfo.website}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[#D4AF37]" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">Headquarters</h3>
                      <p className="text-gray-400">{contactInfo.headquarters}</p>
                    </div>
                  </div>
                </div>

                {/* Image */}
                <div className="hidden lg:block">
                  <img
                    src="https://images.unsplash.com/photo-1568992687947-868a62a9f521"
                    alt="Eaglesway Group Office"
                    className="rounded-lg shadow-2xl w-full"
                  />
                </div>
              </div>

              {/* Contact Form */}
              <div>
                <Card className="bg-gradient-to-b from-gray-900 to-black border-gray-800 p-8">
                  <h3 className="text-2xl font-bold text-white mb-6">
                    Send us a Message
                  </h3>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                        Name *
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        minLength={2}
                        maxLength={100}
                        value={formData.name}
                        onChange={handleChange}
                        className="bg-black border-gray-700 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                        placeholder="Your full name"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                        Email *
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="bg-black border-gray-700 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                        placeholder="your@email.com"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                        Phone (Optional)
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        maxLength={20}
                        value={formData.phone}
                        onChange={handleChange}
                        className="bg-black border-gray-700 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                        placeholder="+1 234 567 8900"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                        Message *
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        minLength={10}
                        maxLength={2000}
                        rows={6}
                        value={formData.message}
                        onChange={handleChange}
                        className="bg-black border-gray-700 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37] resize-none"
                        placeholder="Tell us about your requirements..."
                        disabled={isSubmitting}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.message.length}/2000 characters
                      </p>
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#D4AF37] hover:bg-[#B8941F] text-black font-semibold py-6 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                  
                  <p className="text-xs text-gray-500 mt-4 text-center">
                    We typically respond within 24 business hours
                  </p>
                </Card>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Contact;
